// pages/TaskManagement/DeptTasks.js
//
// Legacy menu: "View My Department's Tasks"
// Round TM-6 (2026) — rebuilt against the legacy-parity backend
// (audit Deliverable 8):
//   tabs are the legacy FLAG/LABOR tabs (Open / Closed / Today /
//   Yesterday), the population is the caller's TRANSITIVE subordinate
//   tree assigned-to only, rows are per (task, assignee) with the
//   assignee's own user-stated totals, Personal descriptions arrive
//   masked from the server, and real paging replaces Take(1000).
// Backend: GET /api/TaskManagement/dept-tasks?tab=&page=&pageSize=
// Access:  Manager+ (IT Admin sees all)

import React, { useCallback, useEffect, useState } from "react";
import { Container, Card, CardBody, Badge, Alert, Button, Table } from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader, EmptyState, ResponsiveTabs, TableSkeleton
} from "../../Components/Common/DashboardKit";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import api from "../../api";

const TABS = [
    { key: "open", label: "Open" },
    { key: "closed", label: "Closed" },
    { key: "today", label: "Activity Today" },
    { key: "yesterday", label: "Activity Yesterday" },
];

const PAGE_SIZE = 100;

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");
const fmtMins = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;

const DeptTasks = () => {
    document.title = "Department Tasks || SSW Technologies Dashboard";
    const navigate = useNavigate();

    const [tab, setTab] = useState("open");
    const [rows, setRows] = useState([]);
    const [scope, setScope] = useState("");
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchPage = useCallback(async (tabKey, pageNo, append) => {
        try {
            setLoading(true);
            setError("");
            const res = await api.get("/TaskManagement/dept-tasks", {
                params: { tab: tabKey, page: pageNo, pageSize: PAGE_SIZE },
            });
            const data = res?.data ?? res;
            const items = Array.isArray(data?.items) ? data.items : [];
            setScope(data?.scope || "");
            setTotal(data?.total ?? items.length);
            setRows((prev) => (append ? [...prev, ...items] : items));
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message
                || "Unable to load department tasks.");
            if (!append) setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setPage(1);
        fetchPage(tab, 1, false);
    }, [tab, fetchPage]);

    const loadMore = () => {
        const next = page + 1;
        setPage(next);
        fetchPage(tab, next, true);
    };

    const exportCsv = () => exportRowsToCsv(
        `dept-tasks-${tab}`,
        rows.map((r) => ({
            TaskID: r.taskId,
            DueDate: fmtDate(r.dueDate),
            AssignedTo: r.assignedToName,
            Description: r.description,
            AssignedBy: r.assignedByName,
            CreatedOn: fmtDate(r.createdOn),
            Activities: r.activitiesLogged,
            TotalMinutes: r.totalTimeMinutes,
        }))
    );

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-team-line"
                    title="My Department's Tasks"
                    subtitle="Every task assigned within your reporting tree"
                    tone="default"
                    breadcrumb={[
                        { label: "Task Management", to: "/task-management" },
                        { label: "Department Tasks" },
                    ]}
                />

                {error && <Alert color="danger">{error}</Alert>}

                <Card className="shadow-sm border-0">
                    <CardBody>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <ResponsiveTabs
                                tabs={TABS}
                                activeKey={tab}
                                onSelect={(k) => setTab(k)}
                            />
                            <div className="d-flex align-items-center gap-2">
                                {scope && (
                                    <Badge color={scope === "ALL" ? "danger" : "info"}>
                                        Scope: {scope}
                                    </Badge>
                                )}
                                <Badge color="light" className="text-muted">
                                    {rows.length} of {total}
                                </Badge>
                                <Button size="sm" color="light" onClick={exportCsv} disabled={rows.length === 0}>
                                    <i className="ri-download-2-line align-bottom me-1"></i>CSV
                                </Button>
                                <Button size="sm" color="light" onClick={() => fetchPage(tab, 1, false)}>
                                    <i className="ri-refresh-line align-bottom"></i>
                                </Button>
                            </div>
                        </div>

                        {loading && rows.length === 0 ? (
                            <TableSkeleton rows={8} cols={7} />
                        ) : rows.length === 0 ? (
                            <EmptyState
                                icon="ri-inbox-line"
                                title="No tasks"
                                message="Nothing in this tab for your team."
                            />
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Due</th>
                                            <th>Assigned To</th>
                                            <th>Description</th>
                                            <th>Assigned By</th>
                                            <th>Created</th>
                                            <th className="text-end">Activities</th>
                                            <th className="text-end">Total Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr
                                                key={`${r.taskId}-${r.assignTo}-${i}`}
                                                role="button"
                                                onClick={() => !r.isPersonal
                                                    && navigate(`/task-management/view-task/${r.taskId}`)}
                                                title={r.isPersonal ? "Personal task" : `Open Task #${r.taskId}`}
                                            >
                                                <td className="fw-semibold">{fmtDate(r.dueDate)}</td>
                                                <td>{r.assignedToName}</td>
                                                <td className="text-truncate" style={{ maxWidth: "380px" }}>
                                                    {r.isPersonal
                                                        ? <span className="text-muted fst-italic">{r.description}</span>
                                                        : r.description}
                                                </td>
                                                <td>{r.assignedByName}</td>
                                                <td>{fmtDate(r.createdOn)}</td>
                                                <td className="text-end">{r.activitiesLogged}</td>
                                                <td className="text-end">{fmtMins(r.totalTimeMinutes)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {rows.length < total && (
                            <div className="text-center mt-3">
                                <Button color="light" onClick={loadMore} disabled={loading}>
                                    {loading ? "Loading..." : `Load more (${total - rows.length} remaining)`}
                                </Button>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default DeptTasks;
